import { IconFont } from '@byecode/ui'
import type { CustomViewLayout, FilterBlockAbstract } from '@lighthouse/core'
import { CUSTOM_VIEW_LAYOUT, type DataSourceAbstract, DataSourceType, FilterMode } from '@lighthouse/core'
import {
    CAN_SELECT_OPTIONS,
    DataGroupConfigure,
    FieldTypeTag,
    filterBlock,
    FindUseType,
    getAppointField,
    getFieldIcon,
    getIsFindUse,
    getIsFindUseInFilter,
    getIsFindUseInSort,
    getViewColumns,
    Group4ByecodeUi,
    notDataSettingInnerTypes,
    PageSizeList,
    useApplicationContext,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext,
    ViewFieldFilterConfigure,
    ViewSortConfigure
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { syncComponentsAtom } from '@/atoms/application/state'
import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pageBlocksAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily,
    selectedBlockCustomViewAtom
} from '@/atoms/page/state'
import { FilterControl } from '@/components/BlockSettings/Common/FilterControl'
import { ListItemMenu } from '@/components/BlockSettings/Common/ListItemMenu'
import { useBlockSettingContext } from '@/contexts/BlockSettingContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

type Configure = {
    canFilter?: boolean
    canLinkedFilter?: boolean
    canSort?: boolean
    canGroup?: boolean
    canPaginate?: boolean
}

const CONFIGURE: Record<CustomViewLayout, Configure> = {
    [CUSTOM_VIEW_LAYOUT.grid]: {
        canFilter: true,
        canLinkedFilter: true,
        canSort: true,
        canGroup: true,
        canPaginate: true
    },
    [CUSTOM_VIEW_LAYOUT.carousel]: {
        canFilter: true,
        canLinkedFilter: true,
        canSort: true
    },
    [CUSTOM_VIEW_LAYOUT.waterfall]: {
        canFilter: true,
        canLinkedFilter: true,
        canSort: true,
        canGroup: true,
        canPaginate: true
    },
    [CUSTOM_VIEW_LAYOUT.weekCalendar]: {
        canFilter: true,
        canLinkedFilter: true,
        canSort: true,
        canGroup: true
    },
    [CUSTOM_VIEW_LAYOUT.monthCalendar]: {
        canFilter: true,
        canLinkedFilter: true
    },
    [CUSTOM_VIEW_LAYOUT.comment]: {
        canFilter: true,
        canLinkedFilter: true,
        canSort: true,
        canPaginate: true
    },
    [CUSTOM_VIEW_LAYOUT.tree]: {
        canFilter: true,
        canLinkedFilter: true,
        canSort: true
    },
    [CUSTOM_VIEW_LAYOUT.multiDimensional]: {
        canFilter: true,
        canLinkedFilter: true
    }
}

interface DataSettingProps {
    dataSource: DataSourceAbstract
    hideFilter?: boolean
    hideSorter?: boolean
    hideGroup?: boolean
    hidePaging?: boolean
    hideFilterController?: boolean
}

/** 数据设置 */
export const DataSetting: React.FC<DataSettingProps> = ({ dataSource }) => {
    const { scope } = useBlockSettingContext()
    const { control, watch } = useFormContext()
    const [layout, direction, wrap, viewFieldSettings] = watch([
        'breakPoint.viewLayout.layout',
        'breakPoint.viewLayout.direction',
        'breakPoint.viewLayout.wrap',
        'viewFieldSettings'
    ])
    const viewLayout = layout as CustomViewLayout
    const isWrappedHorizontalGrid = viewLayout === CUSTOM_VIEW_LAYOUT.grid && direction === 'horizontal' && wrap === true

    const findUseObject = useFindUseObjectContext()
    const { personOptions } = useApplicationContext()
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )
    const customViewDsId = useDeferredValue(
        useAtomData(
            selectedBlockCustomViewAtom,
            useCallback(s => s?.config.pointer, [])
        )
    )
    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])
    const {
        id: dsId,
        schema,
        viewOptions: { tableProps }
    } = dataSource
    const columns = getViewColumns({
        tableProps,
        value: viewFieldSettings,
        schema
    })
    const disabledWithVersion = useIsDisabledWithVersion()
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const options = useMemo(
        () =>
            columns
                .filter(item => CAN_SELECT_OPTIONS.has(item.type))
                .map(item => ({
                    label: item.title,
                    value: item.fieldId,
                    icon: <IconFont fill="var(--color-gray-400)" type={getFieldIcon(item.fieldId, item.type, item.innerType)} size={16} />,
                    extra: <FieldTypeTag type={item.type} innerType={item.innerType} />
                })),
        [columns]
    )
    const filterBlocks = useAtomData(
        scope ? syncComponentsAtom : pageBlocksAtom(pageId),
        useCallback(s => filterBlock(s, block => block.type === 'filter') as FilterBlockAbstract[], [])
    )

    const primaryField = useMemo(() => {
        const field = getAppointField(dataSource, 'ID')
        return field?.id || columns[0]?.fieldId
    }, [columns, dataSource])

    return (
        <Group4ByecodeUi label="数据设置">
            {CONFIGURE[viewLayout].canFilter && (
                <Controller
                    name="filter"
                    control={control}
                    render={({ field: { onChange, value } }) => {
                        const filter = value
                        const filtersLength = filter?.expression?.conditions?.length || 0
                        const isHighLight = getIsFindUseInFilter({
                            findUseObject,
                            data: filter,
                            currentDsId: dsId
                        })
                        return (
                            <ListItemMenu
                                width="auto"
                                highlighting={isHighLight}
                                icon={<IconFont size={16} type="Filter" />}
                                label="筛选数据"
                                positionDependencies={[filter]}
                                value={Boolean(filtersLength) && `${String(filtersLength)} 条筛选项`}
                                popupContent={
                                    <ViewFieldFilterConfigure
                                        dataSource={dataSource}
                                        dataSourceList={dataSourceList}
                                        pagePathParameter={pagePathParameter}
                                        pageQueryParameter={pageQueryParameter}
                                        pageVariables={pageVariables}
                                        pageStateParameter={pageStateParameter}
                                        columns={columns}
                                        primaryField={primaryField}
                                        filter={filter}
                                        viewDsId={customViewDsId}
                                        viewType="custom"
                                        availableFieldBlocksInfo={availableFieldBlocksInfo}
                                        availableFilterBlocksInfo={availableFilterBlocksInfo}
                                        fieldMode={FilterMode.CUSTOM}
                                        paramsMode={FilterMode.CUSTOM}
                                        noSettingInnerType={notDataSettingInnerTypes}
                                        filterBlocks={filterBlocks}
                                        disabled={disabledWithVersion}
                                        // enableFilterVariable
                                        onFilter={onChange}
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    />
                                }
                            />
                        )
                    }}
                />
            )}
            {CONFIGURE[viewLayout].canLinkedFilter && (
                <FilterControl
                    dataSource={dataSource}
                    columns={columns}
                    enableHover
                    filterBlocks={filterBlocks}
                    // value={field.value}
                    // onChange={field.onChange}
                />
            )}

            {CONFIGURE[viewLayout].canSort && (
                <Controller
                    name="sorts"
                    control={control}
                    render={({ field: { onChange, value } }) => {
                        const isHighLight = getIsFindUseInSort({
                            findUseObject,
                            data: value,
                            currentDsId: dsId
                        })
                        return (
                            <ListItemMenu
                                width="auto"
                                highlighting={isHighLight}
                                icon={<IconFont size={16} type="ArrowsDownUp" />}
                                label="排序数据"
                                positionDependencies={[value]}
                                value={Boolean(value?.length) && `${String(value?.length)} 条排序`}
                                popupContent={
                                    <ViewSortConfigure
                                        noSettingInnerType={notDataSettingInnerTypes}
                                        columns={columns}
                                        primaryField={primaryField}
                                        sorters={value}
                                        disabled={disabledWithVersion}
                                        isShowDsName={dataSource.type === DataSourceType.joinDataSource}
                                        onChangeSorter={onChange}
                                    />
                                }
                            />
                        )
                    }}
                />
            )}

            {CONFIGURE[viewLayout].canGroup && (
                <Controller
                    name="groupConfigure"
                    control={control}
                    render={({ field: { value: groupField, onChange: onGroupFieldChange, ...groupFieldField } }) => {
                        const isHighLight = getIsFindUse({
                            findUseObject,
                            type: FindUseType.FIELD,
                            dsId,
                            fieldId: groupField?.groupByFieldId
                        })
                        return (
                            <ListItemMenu
                                label="分组"
                                width={278}
                                highlighting={isHighLight}
                                icon={<IconFont size={16} type="GroupBy" />}
                                value={
                                    groupField?.groupByFieldId &&
                                    schema?.[groupField.groupByFieldId] &&
                                    `按 ${schema?.[groupField.groupByFieldId]?.name} 分组`
                                }
                                dropdownProps={{ style: { borderRadius: 8, boxShadow: 'var(--box-shadow)' } }}
                                positionDependencies={[groupField?.groupConfig]}
                                popupContent={
                                    <DataGroupConfigure
                                        schema={schema}
                                        options={options}
                                        value={groupField}
                                        clearable
                                        enableHiddenTab
                                        enableAllGroup
                                        personOptions={personOptions}
                                        onChange={onGroupFieldChange}
                                        selectProps={{ ...groupFieldField, clearable: true }}
                                    />
                                }
                            />
                        )
                    }}
                />
            )}

            {CONFIGURE[viewLayout].canPaginate && !isWrappedHorizontalGrid && (
                <Controller
                    name="pagination"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <ListItemMenu
                            width="auto"
                            label="每页显示"
                            icon={<IconFont size={16} type="StackSimple" />}
                            value={Boolean(value?.pageSize) && `${String(value.pageSize)} 条数据`}
                            popupContent={
                                <PageSizeList
                                    size={value?.pageSize ?? 0}
                                    custom={value?.custom}
                                    disabled={disabledWithVersion}
                                    onChangePageSize={(size: number, custom = false) => {
                                        onChange({
                                            ...value,
                                            pageSize: size,
                                            custom
                                        })
                                    }}
                                />
                            }
                        />
                    )}
                />
            )}
        </Group4ByecodeUi>
    )
}
